import { User } from '@app/core/models';
import { WorkingStatus } from '../housekeeping/housekeeping.interface';

export interface NewTaskDataModel {
  assignees: TaskAssigneeModel[];
  locations: TaskLocationModel[];
  types: TaskTypeModel[];
  taskStatusList: TaskStatusModel[];
  roomStatuses: RoomStatus[];
  roomTypes: RoomType[];
  cleanTypes: CleanType[];
  typeStatusCleanMap: TypeStatusCleanMapObject[];
  settings: Settings;
}

export interface TypeStatusCleanMapObject {
  cleanTypeId: string;
  mpr: number;
  name: string;
  roomStatusId: string;
  roomTypeId: string;
  roomTypeStatusMapId: string;
}
export interface RoomStatus {
  roomStatusDescription: string;
  roomStatusId: string;
  roomStatusLinenChangeDays: number;
  roomStatusLinenChangeInd: number;
  roomStatusName: string;
}

export interface RoomType {
  id: string;
  name: string;
}

export interface CleanType {
  cleanTypeId: string;
  metaType: string;
  mpr: number;
  name: string;
}

export interface Settings {
  autoSortBoard: boolean;
  defaultStartTime: string;
  entId: string;
  evenSpreadBuffer: number;
  isArrivalDateHidden: boolean;
  isDepartureDateHidden: boolean;
  isFloorOrZoneHidden: boolean;
  isGuestCountHidden: boolean;
  isRewardHidden: boolean;
  isRoomTypeHidden: boolean;
  maxShiftHours: number;
  minShiftHours: number;
  postShiftWorkMinutes: number;
  preShiftWorkMinutes: number;
  publishRoomCleanTask: boolean;
  trackLinenChange: boolean;
  unpaidBreakMinutes: number;
}

export interface TaskAssigneeModel {
  assigneeList?: SimpleAssigneeModel[];
  assigneeTypeId: string;
  assigneeTypeName: string;
}

export interface SimpleAssigneeModel {
  assigneeId: string;
  name: string;
}

export interface TaskLocationModel {
  locationId: string;
  name: string;
}

export interface TaskTypeModel {
  name: string;
  typeId: string;
}

export interface TaskStatusModel {
  name: string;
  statusId: string;
}
export interface TaskAssigneeVM {
  disabled: boolean;
  id: string | null;
  level: number;
  name: string;
  typeId: string;
}

export interface CleanVM {
  cleanType: string;
  targetMinutes: number;
}

export interface CreateTaskBody {
  assigneeId: string;
  assigneeTypeId: string;
  description: string;
  entId: string;
  guestRequested: boolean;
  taskLocationId: string;
  taskTypeId: string;
  title: string;
}

export interface CreateOrEditTaskModel {
  task: Task;
}

export interface UpdateTaskStatusBody {
  taskId: string;
  entId: string;
  taskStatusId: string;
}

export interface TaskStatusUpdatedData {
  modifiedByUserId: string;
  modifiedByUserType: User.Type;
  modifiedOn: string;
  modifiedUserFullName: string;
  taskStatusId: string;
  taskStatusName: string;
}

export interface UpdateTaskStatusPayload {
  taskStatusUpdatedData: TaskStatusUpdatedData;
}

export interface TaskDetailsParams {
  taskId: string;
}

export interface TaskDetailsResponse {
  payload: TaskDetailsPayload;
}

export interface TaskDetailsPayload {
  assignees: TaskAssigneeModel[];
  task: Task;
}

export interface DeleteTaskBody {
  taskId: string;
}

export interface TaskListPayload {
  taskList: Task[];
}

export interface RoomCleanSummary {
  cleanRoomsInInventory: number;
  minimumFullCleansForArrivals: number;
  todayEstimatedArrivals: number;
}

export interface RoomCleanTaskListPayload {
  taskList: RoomCleanTask[];
  summary: RoomCleanSummary;
}

export interface Assignee {
  id?: string;
  typeId: string;
  teamName: string;
  typeName: string;
  user: TaskListUser;
  isPaperOnly?: boolean;
}

export interface TaskListUserModel {
  firstName: string;
  lastName: string;
  userId: string;
  userType: User.Type;
}

export interface PubNubTask {
  assignee: PubNubAssignee;
  createdByUserId: string;
  createdByUserType: User.Type;
  createdOn: string;
  createdUserFullName: string;
  description: string;
  details: RoomCleanDetails;
  entId: string;
  guestRequested: boolean;
  modifiedByUserId: string;
  modifiedByUserType: User.Type;
  modifiedOn: string;
  modifiedUserFullName: string;
  taskCreatedDate: string;
  taskId: string;
  taskLocationId: string;
  taskLocationName: string;
  taskNumber: number;
  taskStatusId: string;
  taskStatusName: string;
  taskTypeId: string;
  taskTypeName: 'Room Clean' | 'Service Request' | 'Task' | 'Work Order';
  title: string;
  taskNotes: Note[] | null;
  taskHistory: TaskHistoryItem[] | null;
}

export interface PubNubAssignee {
  assigneeId: string;
  assigneeTypeId: string;
  isWriteInName: boolean;
  taskAssigneeTeamName: string;
  taskAssigneeTypeName: string;
  user: PubNubUser;
  isPaperOnly?: boolean;
}

export interface PubNubUser {
  firstName: string;
  heid: string;
  lastName: string;
  userId: string;
  userType: string;
}

export interface TaskListUser {
  firstName: string;
  lastName: string;
  userId: string;
  userType: string;
}

export enum Status {
  OPEN = 'Open',
  IN_PROGRESS = 'In Progress',
  DONE = 'Done',
}

export interface GetTaskInboxChannelSuccess extends Response {
  payload: {
    taskInboxChannelName: string;
  };
}

export interface EditTaskBody {
  assigneeId: string;
  assigneeTypeId: string;
  description: string;
  entId: string;
  guestRequested: boolean;
  taskId: string;
  taskLocationId: string;
  taskStatusId: string;
  taskTypeId: string;
  title: string;
}

export enum TaskFilter {
  ASSIGNED_UNFINISHED_TASKS = 'Assigned Unfinished Tasks',
  MY_RECENTLY_FINISHED_ROOM_CLEAN_TASKS = 'My Recently Finished Room Clean Tasks',
  MY_UNFINISHED_ROOM_CLEAN_TASKS = 'My Unfinished Room Clean Tasks',
  OPEN_TASKS = 'Open Tasks',
  RECENTLY_FINISHED_TASKS = 'Recently Finished Tasks',
  UNASSIGNED_OPEN_TASKS = 'Unassigned Open Tasks',
  UNFINISHED_GUEST_REQUESTS = 'Unfinished Guest Requests',
}

export enum RoomCleanTaskStatus {
  CLEANING = 'Cleaning',
  DECLINED_SERVICE = 'Declined Service',
  DIRTY = 'Dirty',
  DND = 'DND',
  DONE = 'Done',
  RETURN_LATER = 'Return Later',
  INSPECTED = 'Inspected',
}

export interface TaskHistoryItem {
  changeType: string;
  firstName: string;
  lastName: string;
  modifiedOn: string;
  newValue: string;
  previousValue: string;
  userName: string;
}

export interface TaskBase {
  assignee: Assignee;
  createdByUserId: string;
  createdByUserType: User.Type;
  createdOn: string;
  createdUserFullName: string | null;
  description: string | null;
  entId: string;
  guestRequested: boolean;
  id: string;
  locationId: string;
  locationName: string;
  modifiedByUserId: string;
  modifiedByUserType: User.Type;
  modifiedOn: string;
  modifiedUserFullName: string | null;
  statusId: string;
  statusName: string;
  taskCreatedDate?: string;
  taskHistory: TaskHistoryItem[];
  taskNumber: number;
  title: string;
  typeId: string;
  notes: Note[];
}

export interface GeneralTask extends TaskBase {
  typeName: 'Task';
}

export interface RoomCleanDetails {
  arrivalDate: string | null;
  changeLinen: boolean | null;
  cleanTypeId: string;
  cleanTypeName: string;
  departureDate: string | null;
  floor: string;
  guestCount: string | null;
  reward: string;
  roomInventoryId: string;
  roomNumber: string;
  roomStatusId: string;
  roomStatusName: string;
  roomTypeId: string;
  roomTypeName: string;
  sortOrder: number;
  targetMinutes: number;
}

export interface RoomCleanTask extends TaskBase {
  typeName: 'Room Clean';
  details: RoomCleanDetails;
}

export interface ServiceRequestTask extends TaskBase {
  typeName: 'Service Request';
}

export interface WorkOrderTask extends TaskBase {
  typeName: 'Work Order';
}

export type Task =
  | GeneralTask
  | RoomCleanTask
  | ServiceRequestTask
  | WorkOrderTask;

export type TaskList = Task[];

export type RoomCleanTaskList = RoomCleanTask[];

export interface TaskBaseVM {
  assignee: Assignee;
  assigneeName: string;
  createdOn: string;
  formattedCreatedOn: string;
  guestRequested: boolean;
  id: string;
  isAssignedToCurrentUser: boolean;
  locationName: string;
  modifiedOn: string;
  statusId: string;
  statusName: string;
  title: string;
  typeId: string;
  notes?: Note[];
}

export interface GeneralTaskVM extends TaskBaseVM {
  typeName: 'Task';
}

export interface RoomCleanTaskVM extends TaskBaseVM {
  typeName: 'Room Clean';
  roomTypeName: string;
  roomStatusName: string;
  cleanTypeName: string;
  changeLinen: boolean | null;
  statusName: RoomCleanTaskStatus;
  roomNumber: string;
  sortOrder: number;
  hasUnreadNote: boolean;
  targetMinutes: number;
}

export interface ServiceRequestTaskVM extends TaskBaseVM {
  typeName: 'Service Request';
}

export interface WorkOrderTaskVM extends TaskBaseVM {
  typeName: 'Work Order';
}

export type TaskVM =
  | GeneralTaskVM
  | RoomCleanTaskVM
  | ServiceRequestTaskVM
  | WorkOrderTaskVM;

export type TaskListVM = TaskVM[];

export interface RealtimeRoomsListViewItem {
  taskId: string;
  roomNumber: string;
  roomType: string;
  pmsStatus: string;
  cleanType: string;
  changeLinen: boolean;
  assignee: string;
  cleanStatus: RoomCleanTaskStatus;
  isPaperOnly: boolean;
  sortOrder: number;
  notes?: Note[];
  hasUnreadNote: boolean;
}

export interface RealtimeRoomsRoomAttendant {
  name: string;
  roomCleansDone: number;
  totalRoomCleans: number;
  workingStatus?: WorkingStatus;
  isPaperOnly: boolean;
}

export interface TaskDetailsBaseVM extends TaskBaseVM {
  canMarkAsDone: boolean;
  canStartProgress: boolean;
  createdUserFullName: string;
  description: string;
  modifiedUserFullName: string;
  titleHeader: string;
}

export interface GeneralTaskDetailsVM extends TaskDetailsBaseVM {
  typeName: 'Task';
}

export interface RoomCleanTaskDetailsVM extends TaskDetailsBaseVM {
  arrivalDate: string;
  changeLinen: boolean;
  cleanTypeName: string;
  departureDate: string;
  floor: string;
  guestCount: string;
  reward: string;
  roomNumber: string;
  roomStatusName: string;
  roomStatusId: string;
  roomTypeName: string;
  sortOrder: number;
  targetMinutes: number;
  typeName: 'Room Clean';
  dndMarkedAtTimes: string[];
}

export interface ServiceRequestTaskDetailsVM extends TaskDetailsBaseVM {
  typeName: 'Service Request';
}

export interface WorkOrderTaskDetailsVM extends TaskDetailsBaseVM {
  typeName: 'Work Order';
}

export type TaskDetailsVM =
  | GeneralTaskDetailsVM
  | RoomCleanTaskDetailsVM
  | ServiceRequestTaskDetailsVM
  | WorkOrderTaskDetailsVM;

export interface UpdateRoomCleanTaskAssigneeBody {
  entId: string;
  taskId: string;
  assigneeId: string;
  assigneeTypeId: string;
}

export interface UpdateRoomCleanTaskAssigneePayload {
  task: RoomCleanTask;
}

export interface Note {
  createdOn: string;
  firstName: string;
  id: string;
  isMyNote: boolean;
  lastName: string;
  text: string;
  username: string;
  taskNoteRead?: string | null;
}

export interface CreateOrEditRoomCleanTaskNoteBody {
  entId: string;
  id: string | null;
  taskId: string;
  value: string;
}

export interface RealTimeTaskListSort {
  column: RealtimeRoomsColumn | null;
  orderBy: 'asc' | 'desc' | null;
}

export type RealtimeRoomsColumn =
  | 'roomNumber'
  | 'hasUnreadNote'
  | 'cleanStatus'
  | 'assignee'
  | 'changeLinen'
  | 'cleanType';
